import "./default.style.css";

import React, {startTransition} from 'react';
import DataComponent, {executeComponentCallback, executeComponentCallbackPromise} from 'Core/components/DataComponent';
import PropTypes from "prop-types";
import {v4} from "uuid";
import {set, get, isEqual, omit, find} from 'lodash';
import {COMPARATOR, LOGICAL_OPERATOR} from 'Core/const/global';
import {FormField} from 'Core/components/advanced/FormWrapper';
import SelectInput from 'Core/components/input/SelectInput';
import {FORM_FIELD_LABEL_POSITION} from 'Core/components/advanced/FormWrapper/FormField';
import TextInput from 'Core/components/input/TextInput';
import {
	CONDITION_LIST_FIELD_DISPLAY_TYPE,
	CONDITION_LIST_FIELD_DISPLAY_TYPES,
	CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE,
	CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE_COMPARATOR_MAP, 
	CONDITION_LIST_FIELD_ITEM_EMPTY_VALUES,
	CONDITION_LIST_VALUE_CLEAR_TYPE,
	CONDITION_LIST_VALUE_CLEAR_TYPES
} from '../const';
import Button, {BUTTON_DISPLAY_TYPE, BUTTON_STYLE} from 'Core/components/display/Button';
import {icon_font_create_symbol, icon_font_delete_symbol} from 'Config/app';
import CheckboxInput from 'Core/components/input/CheckboxInput';
import {getArray, getBoolean, getString, isset} from 'Core/helpers/data';
import NumberInput from 'Core/components/input/NumberInput';
import DateInput from 'Core/components/input/DateInput';
import SelectAsyncInput from 'Core/components/input/SelectAsyncInput';
import TimeInput from 'Core/components/input/TimeInput';
import DatetimeInput from 'Core/components/input/DatetimeInput';
import BooleanSelectComponent from 'Core/components/input/BooleanSelectInput';
import TooltipDropdown from 'Core/components/display/TooltipDropdown';
import Label from 'Core/components/display/Label';
import YearDaySelectInput from 'Core/components/input/YearDaySelectInput';

class ConditionListItem extends DataComponent {
	constructor(props) {
		super(props, {
			/**
			 * @type {ConditionListItemDataObject}
			 */
			data: new ConditionListItemDataObject(),
		}, {
			translationPath: 'ConditionListComponent.ListItem',
			domPrefix: 'condition-list-item-component',
			enableLoadOnDataPropChange: true,
			optimizedUpdate: true,
			optimizedUpdateIgnoreProps: [
				// Ignore event props because anonymous functions (that are usually used for them) are generated on each 
				// parent component render, so they are always different and optimization will not work. This is true for 
				// any function prop.
				'onChange', 'onEnterKey', 'onSelect', 'onDelete', 'onInsertItem', 'onInsertGroup', 
				'onValidationErrorsChange'
			],
			// Include only state fields relevant for rendering the component
			// @note This component extends and abstract component that might have some state fields.
			optimizedUpdateIncludeState: ['data', 'errors'],
		});
		
		// Data methods
		this.getComparators = this.getComparators.bind(this);
		
		// Render methods
		this.renderCustomFieldComponent = this.renderCustomFieldComponent.bind(this);

		// Data change handling methods
		this.handleFieldSelectChangeAndUpdate = this.handleFieldSelectChangeAndUpdate.bind(this);
		this.handleConditionValueChangeAndUpdate = this.handleConditionValueChangeAndUpdate.bind(this);
		this.handleComparatorChangeAndUpdate = this.handleComparatorChangeAndUpdate.bind(this);
	}

	componentDidUpdate(prevProps, prevState, snapshot) {
		return super.componentDidUpdate(prevProps, prevState, snapshot)
			// Update errors in local state based on 'errors' prop.
			.then(async () => {
				if (!isEqual(prevProps.errors, this.props.errors)) await this.setValidationErrors('', this.props.errors);
				return this.state;
			})
			// Update selected field option label if field is a select input when field select options change
			// @note This is done mainly to update the selected option label when language changes and labels are 
			// translated by the parent component.
			.then(async () => {
				if (this.getProp('fieldDisplayType') === CONDITION_LIST_FIELD_DISPLAY_TYPE.SELECT) {
					const field = this.getValue('field');
					const fieldSelectOptions = getArray(this.props, 'fieldDisplayOptions.options');
					const prevFieldSelectOptions = getArray(prevProps, 'fieldDisplayOptions.options');

					if (!isEqual(fieldSelectOptions, prevFieldSelectOptions)) {
						const selectedOption = find(fieldSelectOptions, {value: get(field, 'value')});
						if (!!selectedOption) {
							await this.handleValueChangeAndUpdate(
								'field', 
								{...field, label: getString(selectedOption, 'label')}
							);
						}
					}
				}
				return this.state;
			});
	}

	/**
	 * Clear validation errors for a specific component's field
	 *
	 * @param {string|string[]} path - Path of the field inside the data object to ser the errors array for. If not
	 * specified, errors for all fields will be cleared.
	 * @return {Promise<Object>}
	 */
	clearValidationErrors(path = '') {
		return new Promise(resolve => {
			startTransition(() => {
				executeComponentCallbackPromise(
					this.props.onValidationErrorsChange,
					this.getValue('GUIID'),
					path ? omit(this.state.errors, path) : {}
				).then(resolve);
			});
		});
	}


	// Data methods -----------------------------------------------------------------------------------------------------
	/**
	 * Get data to load into local component's state
	 * @description Create and return data that can be loaded directly into local component's state based on the raw
	 * external data (usually sent through props). In some sense this is a method that maps external data into format
	 * that component can use in its local state. This method should return data in the same format as 'getData' method.
	 * @note This method will not mutate the passed data.
	 *
	 * @param {any} rawData - External data that will be used to create local component's state compatible data.
	 * @return {ConditionListItemDataObject} Local component's state compatible data or null if data could not be loaded.
	 */
	getDataToLoad(rawData) {
		const {index} = this.props;
		let result = super.getDataToLoad(rawData);
		
		// Clear logical operator for the first item in the list
		if (index === 0) set(result, 'logicalOperator', '');
		
		return result;
	}

	/**
	 * Get data that will be returned to the parent component
	 * @description Create and return data that will be returned to paren component (usually through onChange event)
	 * based on the local component's raw state data
	 * @note This method will not mutate the passed data.
	 *
	 * @return {ConditionListItemDataObject} Data that will be returned to the parent component.
	 */
	getDataToReturn() {
		const {index} = this.props;
		let result = super.getDataToReturn();

		// Clear logical operator for the first item in the list
		if (index === 0) set(result, 'logicalOperator', '');

		return result;
	}
	
	/**
	 * Trigger component's onChange event with component's main data as param
	 * @param {Event} [event] - Optional JavaScript event that triggered the update.
	 * @return {any|null} Any return value from 'onChange' event function or null.
	 */
	update(event) { return executeComponentCallback(this.props.onChange, this.getDataToReturn()); }

	/**
	 * Get comparators to show based on selected field item
	 * @param {ConditionListFieldItemDataObject} field - Field item.
	 * @return {ComparatorSymbol[]}
	 */
	getComparators(field) {
		const {comparatorDisplayTypeMap} = this.props;
		
		if (field) {
			// Use custom list of supported comparators if it is specified for this field item
			if (isset(field.supportedComparators)) {
				return getArray(field, 'supportedComparators');
			}
			// Calculate the list of supported comparators based on field item display type and comparator display type map
			else {
				const displayType = getString(field, 'displayType', CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.TEXT);

				// If map has this field's display type
				if (comparatorDisplayTypeMap.hasOwnProperty(displayType)) {
					return getArray(comparatorDisplayTypeMap, displayType);
				}
				// If map has a default list of comparators
				else if (comparatorDisplayTypeMap.hasOwnProperty('DEFAULT')) {
					return getArray(comparatorDisplayTypeMap, 'DEFAULT');
				}
				// If map does not have a default list of comparators
				else {
					return CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE_COMPARATOR_MAP.DEFAULT;
				}
			}
		}

		// Default list
		return CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE_COMPARATOR_MAP.DEFAULT;
	}

	
	// Data change handling methods -------------------------------------------------------------------------------------
	/**
	 * Handle field change when field is a select input component
	 * @param {Object} selectedOption - Selected option.
	 * @return {Promise<Object>}
	 */
	handleFieldSelectChangeAndUpdate(selectedOption) {
		const {
			fieldItemLabelSelector, fieldItemValueSelector, fieldItemDisplayTypeSelector, fieldItemDisplayOptionsSelector,
			fieldItemSupportedComparatorsSelector, clearValueType, emptyValues
		} = this.props;
		
		const fieldItemDisplayType = get(selectedOption, fieldItemDisplayTypeSelector);
		const prevFieldItemDisplayType = this.getValue('field.displayType');
		const field = new ConditionListFieldItemDataObject(
			get(selectedOption, fieldItemLabelSelector),
			get(selectedOption, fieldItemValueSelector),
			get(selectedOption, 'data', {}),
			get(selectedOption, fieldItemDisplayTypeSelector),
			get(selectedOption, fieldItemDisplayOptionsSelector),
			get(selectedOption, fieldItemSupportedComparatorsSelector)
		);

		// Do nothing if field did not change
		if (isEqual(field, this.getValue('field'))) return this.state;
		
		return this.setState(prevState => ({
			data: {
				...prevState.data,
				field,
				// Clear value based on 'clearValueType' prop
				value: (
					// Clear the value when field item display type changes
					clearValueType === CONDITION_LIST_VALUE_CLEAR_TYPE.ON_TYPE_CHANGE ?
						fieldItemDisplayType !== prevFieldItemDisplayType ?
							emptyValues[fieldItemDisplayType] :
							prevState.data.value
					// Clear the value when field changes
					: clearValueType === CONDITION_LIST_VALUE_CLEAR_TYPE.ALWAYS ?
						emptyValues[fieldItemDisplayType]
					// Do not clear the value
					: prevState.data.value
				)
			}
		}))
			.then(() => this.update());
	}

	/**
	 * Handle condition value change
	 * @param {any} value - New condition value
	 * @return {Promise<Object>}
	 */
	handleConditionValueChangeAndUpdate(value) {
		return this.handleValueChangeAndUpdate('value', value);
	}
	
	/**
	 * Handle comparator value change
	 * @param {ComparatorSymbol} selectedComparator
	 * @return {Promise<Object>}
	 */
	async handleComparatorChangeAndUpdate(selectedComparator) {
		const {fieldItemDisplayTypeSelector, emptyValues} = this.props;
		const fieldItemDisplayType = get(this.getValue('field'), fieldItemDisplayTypeSelector);
		const currentComparator = this.getValue('comparator', null);
		
		if (currentComparator !== selectedComparator) {
			// If comparator changed from or to 'BTW' (between), clear the value since between uses an array and other 
			// values don't
			if ([currentComparator, selectedComparator].some(c => c === COMPARATOR.BTW)) {
				await this.setState(prevState => ({data: {...prevState.data, value: emptyValues[fieldItemDisplayType]}}));
			}
		}
		
		return this.handleValueChangeAndUpdate('comparator', selectedComparator);
	}


	// Render methods ---------------------------------------------------------------------------------------------------
	/**
	 * Render custom field component
	 * @description Custom component will receive 'value', 'label', 'data' and any other props defined in 
	 * fieldDisplayOptions. It must have an 'onChange' event that should return and object with at least 'value', 
	 * 'label' and 'data' properties.
	 * @return {JSX.Element}
	 */
	renderCustomFieldComponent() {
		const {fieldDisplayOptions, readOnly} = this.props;
		const FieldCustomComponent = this.props.fieldCustomComponent;
		
		return (
			<FieldCustomComponent
				readOnly={readOnly}
				value={this.getValue('field.value')}
				label={this.getValue('field.label')}
				data={this.getValue('field.data')}
				onChange={v => 
					this.setSubState('data.field', v)
						.then(state => (
							this.getOption('clearValidationErrorsOnChange') === true ?
								this.clearValidationErrors('field') :
								Promise.resolve(state)
						))
						.then(() => this.update())
				}
				{...fieldDisplayOptions}
			/>
		);
	}
	
	render() {
		const {
			styleName, className, index, isLast, selectable, selected, fieldDisplayType, fieldDisplayOptions,
			fieldItemLabelSelector, fieldItemValueSelector, enableParenthesis, disabled, showActions, readOnly, 
		} = this.props;

		return (
			<div
				id={this.getDomId()}
				className={
					`${this.getOption('domPrefix')} ${styleName}-style ${className} ${selectable ? 'selectable' : ''} ` +
					`${readOnly ? 'read-only' : ''} ${index === 0 ? 'first' : ''} ${isLast ? 'last' : ''}`
				}
				data-guiid={this.getValue('GUIID')}
			>
				{
					!readOnly && selectable ?
						<div className={`select-wrapper`}>
							<CheckboxInput
								checked={getBoolean(selected)}
								size={18}
								disabled={disabled}
								onChange={() => executeComponentCallback(this.props.onSelect, this.getValue('GUIID'))}
							/>
						</div>
						: null
				}
				
				<FormField 
					className={'field-wrapper'} 
					labelPosition={FORM_FIELD_LABEL_POSITION.NONE}
					errorMessages={this.getValidationErrors('field')}
				>
					{
						fieldDisplayType === CONDITION_LIST_FIELD_DISPLAY_TYPE.TEXT ?
							<TextInput
								readOnly={readOnly}
								value={this.getValue('field.value')}
								onChange={e => this.handleValueChangeAndUpdate('field', new ConditionListFieldItemDataObject(
									getString(e.target, 'value'), getString(e.target, 'value')
								))}
								disabled={disabled}
							/>
						: fieldDisplayType === CONDITION_LIST_FIELD_DISPLAY_TYPE.SELECT ?
							<SelectInput
								simpleValue={false}
								isClearable={!readOnly}
								isDisabled={disabled || readOnly}
								isSearchable={!readOnly}
								{...fieldDisplayOptions}
								getOptionLabel={o => get(o, fieldItemLabelSelector)}
								getOptionValue={o => get(o, fieldItemValueSelector)}
								value={this.getValue('field')}
								onChange={this.handleFieldSelectChangeAndUpdate}
							/>
						: fieldDisplayType === CONDITION_LIST_FIELD_DISPLAY_TYPE.ASYNC_SELECT ?
							<SelectAsyncInput
								isClearable={!readOnly}
								isDisabled={disabled || readOnly}
								isSearchable={!readOnly}
								{...fieldDisplayOptions}
								getOptionLabel={o => get(o, fieldItemLabelSelector)}
								getOptionValue={o => get(o, fieldItemValueSelector)}
								value={this.getValue('field')}
								onChange={this.handleFieldSelectChangeAndUpdate}
							/>
						: fieldDisplayType === CONDITION_LIST_FIELD_DISPLAY_TYPE.CUSTOM ?
							this.renderCustomFieldComponent()
						: null
					}
				</FormField>
				
				<FormField className={`comparator-wrapper`} labelPosition={FORM_FIELD_LABEL_POSITION.NONE}>
					<SelectInput
						isClearable={!readOnly}
						isDisabled={disabled || readOnly}
						isSearchable={!readOnly}
						menuPlacement="auto"
						options={this.getComparators(this.getValue('field')).map(
							c => ({label: this.t(c, 'comparators'), value: c}))
						}
						value={this.getValue('comparator', null)}
						onChange={this.handleComparatorChangeAndUpdate}
					/>
				</FormField>

				<FormField 
					className={`value-wrapper`} 
					labelPosition={FORM_FIELD_LABEL_POSITION.NONE}
					errorMessages={this.getValidationErrors('value')}
				>
					{
						this.getValue('field.displayType') === CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.TEXT ?
							<TextInput
								name="value"
								showClearButton={true}
								value={this.getValue('value')}
								disabled={disabled || readOnly}
								readOnly={readOnly}
								onChange={e => this.handleConditionValueChangeAndUpdate(getString(e, 'target.value'))}
								onEnterKey={() => this.update()}
								{...this.getValue('field.displayOptions', {})}
							/>
						: this.getValue('field.displayType') === CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.NUMBER || 
							this.getValue('field.displayType') === CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.INTEGER ?
							<NumberInput
								value={this.getValue('value')}
								showClearButton={true}
								disabled={disabled || readOnly}
								readOnly={readOnly}
								onChange={this.handleConditionValueChangeAndUpdate}
								onEnterKey={() => this.update()}
								intOnly={
									this.getValue('field.displayType') === CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.INTEGER
								}
								{...this.getValue('field.displayOptions', {})}
							/>
						: this.getValue('field.displayType') === CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.DATE ?
							<DateInput
								value={this.getValue('value')}
								disabled={disabled || readOnly}
								readOnly={readOnly}
								onChange={this.handleConditionValueChangeAndUpdate}
								onEnterKey={() => this.update()}
								{...this.getValue('field.displayOptions', {})}
							/>
						: this.getValue('field.displayType') === CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.TIME ?
							<TimeInput
								name="value"
								showClearButton={true}
								value={this.getValue('value')}
								disabled={disabled || readOnly}
								readOnly={readOnly}
								onChange={this.handleInputChangeAndUpdate}
								onEnterKey={() => this.update()}
								{...this.getValue('field.displayOptions', {})}
							/>
						: this.getValue('field.displayType') === CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.DATETIME ?
							<DatetimeInput
								value={this.getValue('value')}
								disabled={disabled || readOnly}
								readOnly={readOnly}
								onChange={this.handleConditionValueChangeAndUpdate}
								onEnterKey={() => this.update()}
								{...this.getValue('field.displayOptions', {})}
							/>
						: this.getValue('field.displayType') === CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.BOOLEAN ?
							<BooleanSelectComponent
								value={this.getValue('value')}
								isClearable={!readOnly}
								isDisabled={disabled || readOnly}
								isSearchable={!readOnly}
								onChange={this.handleConditionValueChangeAndUpdate}
								onEnterKey={() => this.update()}
								{...this.getValue('field.displayOptions', {})}
							/>
						: this.getValue('field.displayType') === CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.SELECT ?
							<SelectInput
								value={this.getValue('value')}
								isClearable={!readOnly}
								isDisabled={disabled || readOnly}
								isSearchable={!readOnly}
								onChange={this.handleConditionValueChangeAndUpdate}
								onEnterKey={() => this.update()}
								{...this.getValue('field.displayOptions', {})}
							/>
						: this.getValue('field.displayType') === CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.ASYNC_SELECT ?
							<SelectAsyncInput
								value={this.getValue('value')}
								isClearable={!readOnly}
								isDisabled={disabled || readOnly}
								isSearchable={!readOnly}
								onChange={this.handleConditionValueChangeAndUpdate}
								onEnterKey={() => this.update()}
								{...this.getValue('field.displayOptions', {})}
							/>
						: this.getValue('field.displayType') === CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.DAY ?
							<YearDaySelectInput
								value={this.getValue('value')}
								isClearable={!readOnly}
								isDisabled={disabled || readOnly}
								isSearchable={!readOnly}
								isMulti={(this.getValue('comparator', null) === COMPARATOR.BTW)}
								multiLimit={2}
								isLeapYear={true}
								valueFormat="MMdd"
								onChange={this.handleConditionValueChangeAndUpdate}
								onEnterKey={() => this.update()}
								placeholder=""
								{...this.getValue('field.displayOptions', {})}
							/>
						: null
					}
				</FormField>

				{!readOnly && showActions ?
					<>
						<div className={`remove-wrapper`}>
							<Button
								icon={icon_font_delete_symbol}
								displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
								displayStyle={BUTTON_STYLE.SUBTLE}
								disabled={disabled}
								onClick={() => executeComponentCallback(this.props.onDelete, this.getValue('GUIID'))}
							/>
						</div>

						<div className={`insert-wrapper`}>
							{enableParenthesis ?
								<TooltipDropdown
									element="div"
									label={
										<Button
											icon={icon_font_create_symbol}
											displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
											displayStyle={BUTTON_STYLE.SUBTLE}
											disabled={disabled}
										/>
									}
									position="bottom-end"
									duration={0}
									distance={0}
									disabled={disabled}
								>
									<div
										className="dropdown-item"
										onClick={() => executeComponentCallback(this.props.onInsertItem, index, 'above')}
									>
										<Label icon="chevron-up" content={this.t('insert_item_above')} supportHtml={true}/>
									</div>
									<div
										className="dropdown-item"
										onClick={() => executeComponentCallback(this.props.onInsertItem, index + 1, 'below')}
									>
										<Label icon="chevron-down" content={this.t('insert_item_below')} supportHtml={true}/>
									</div>
									<hr/>
									<div
										className="dropdown-item"
										onClick={() => executeComponentCallback(this.props.onInsertGroup, index, 'above')}
									>
										<Label icon="chevron-up" content={this.t('insert_group_above')} supportHtml={true}/>
									</div>
									<div
										className="dropdown-item"
										onClick={() => executeComponentCallback(this.props.onInsertGroup, index + 1, 'below')}
									>
										<Label icon="chevron-down" content={this.t('insert_group_below')} supportHtml={true}/>
									</div>
								</TooltipDropdown>
								:
								<TooltipDropdown
									element="div"
									label={
										<Button
											icon={icon_font_create_symbol}
											displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
											displayStyle={BUTTON_STYLE.SUBTLE}
											disabled={disabled}
										/>
									}
									position="bottom-end"
									duration={0}
									distance={0}
									disabled={disabled}
								>
									<div
										className="dropdown-item"
										onClick={() => executeComponentCallback(this.props.onInsertItem, index, 'above')}
									>
										<Label icon="chevron-up" content={this.t('insert_item_above')} supportHtml={true}/>
									</div>
									<div
										className="dropdown-item"
										onClick={() => executeComponentCallback(this.props.onInsertItem, index + 1, 'below')}
									>
										<Label icon="chevron-down" content={this.t('insert_item_below')} supportHtml={true}/>
									</div>
								</TooltipDropdown>
							}
						</div>
					</>
					: null
				}
			</div>
		);
	}
}

// Data objects
/**
 * Condition list item data object
 */
export class ConditionListItemDataObject {
	/**
	 * @param {LogicalOperator|''} [logicalOperator=''] - Condition logical operator. First condition in the list does
	 * not have this. See 'LOGICAL_OPERATOR' global const.
	 * @param {ConditionListFieldItemDataObject} [field] - Condition field.
	 * @param {ConditionListComparator} [comparator='EQ'] - Condition comparator. See 'COMPARATOR' global const.
	 * @param {any} [value] - Condition value.
	 * @param {number} [openParenthesis=0] - Number of open parenthesis before this item.
	 * @param {number} [closeParenthesis=0] - Number of closed parenthesis after this item.
	 * @param {string} [id] - Condition item id.
	 * @param {number} [ordinal] - Condition item list index.
	 * @param {Object} [data={}] - Condition item data.
	 * @param {string|null} [GUIID] - GUI ID. If not specified or empty, unique GUID will be used.
	 */
	constructor(
		logicalOperator = LOGICAL_OPERATOR.AND, field, comparator = COMPARATOR.EQ, value, openParenthesis = 0,
		closeParenthesis = 0, id, ordinal, data = {}, GUIID
	) {
		this.GUIID = (GUIID ? GUIID : v4());
		this.logicalOperator = logicalOperator;
		this.field = (field ? field : new ConditionListFieldItemDataObject());
		this.comparator = comparator;
		this.value = value;
		this.openParenthesis = openParenthesis;
		this.closeParenthesis = closeParenthesis;
		this.id = id;
		this.ordinal = ordinal;
		this.data = data;
	}
}

/**
 * Condition list item field item data object
 */
export class ConditionListFieldItemDataObject {
	/**
	 * @param {string} [label=''] - Field item label.
	 * @param {any} [value] - Field item value.
	 * @param {Object} [data={}] - Field item data.
	 * @param {ConditionListFieldItemDisplayType} [displayType='TEXT'] - Field item display type used for rendering the 
	 * condition value element.
	 * @param {Object} [displayOptions={}] - Field item display options used for rendering the condition value element. 
	 * @param {ComparatorSymbol[]|string[]} [supportedComparators] - Custom list of supported comparators. If not 
	 * specified comparators will be selected based on display type map.
	 */
	constructor(
		label = '', value, data = {}, displayType = CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.TEXT, displayOptions = {}, 
		supportedComparators
	) {
		this.label = label;
		this.value = value;
		this.data = data;
		this.displayType = (displayType ? displayType : CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE.TEXT);
		this.displayOptions = displayOptions;
		this.supportedComparators = supportedComparators;
	}
}


/**
 * Define component's own props that can be passed to it by parent components
 */
ConditionListItem.propTypes = {
	// Component style name
	// @description Component style name is a name of the style that will be used to determine the CSS used to style the
	// component.
	styleName: PropTypes.string,
	// Component's wrapper element class attribute
	className: PropTypes.string,

	// Flag that determines whether the component is read-only
	readOnly: PropTypes.bool,
	// Flag that determines if condition item will be selectable
	// @note Selectable items can be removed or other actions can be performed on them by adding custom toolbar buttons.
	selectable: PropTypes.bool,
	// Flag that determines if this condition item is selected or not
	selected: PropTypes.bool,
	// Flag that specifies if list item actions (delete, add, ...) should be rendered
	showActions: PropTypes.bool,
	// Flag that specifies if the whole component will be disabled
	disabled: PropTypes.bool,
	
	// Main component data
	// @type {ConditionListItemDataObject}
	data: PropTypes.object,
	// Errors to show for item
	errors: PropTypes.object,
	// Index of the item in the list
	// @note Starts from 0.
	index: PropTypes.number,
	// Flag that specifies if this is the last item in the list
	isLast: PropTypes.bool,
	// Map of the comparators based on field display type
	// @see CONDITION_LIST_FIELD_ITEM_DISPLAY_TYPE_COMPARATOR_MAP const for reference.
	comparatorDisplayTypeMap: PropTypes.object,
	// Flag that specifies if parenthesis will be enabled
	enableParenthesis: PropTypes.bool,
	// Specifies when condition value will be cleared
	clearValueType: PropTypes.oneOf(CONDITION_LIST_VALUE_CLEAR_TYPES),
	// Object defining empty values for each field item display type
	// @note If not specified, default empty values wil be used (@see CONDITION_LIST_FIELD_ITEM_EMPTY_VALUES).
	// @type {Object<ConditionListFieldItemDisplayType, any>}
	emptyValues: PropTypes.object,
	
	// Display type used to render the field element 
	fieldDisplayType: PropTypes.oneOf(CONDITION_LIST_FIELD_DISPLAY_TYPES),
	// Condition field component props
	// @note This will depend on condition field display type ('fieldDisplayType' prop) but essentially use this to 
	// specify props for component used to render the condition field.
	fieldDisplayOptions: PropTypes.object,
	// Condition field custom component if display type is custom (CONDITION_LIST_FIELD_DISPLAY_TYPE_CUSTOM)
	fieldCustomComponent: PropTypes.elementType,
	// Property name of the condition field item label in field item object
	fieldItemLabelSelector: PropTypes.string,
	// Property name of the condition field item value in field item object
	fieldItemValueSelector: PropTypes.string,
	// Property name of the condition field item display type in field item object
	fieldItemDisplayTypeSelector: PropTypes.string,
	// Property name of the condition field item display options in field item object
	fieldItemDisplayOptionsSelector: PropTypes.string,
	// Property name of the condition field item supported comparators in field item object
	fieldItemSupportedComparatorsSelector: PropTypes.string,

	// Events
	onSelect: PropTypes.func, // Arguments: GUI ID of the item.
	onChange: PropTypes.func, // Arguments: {ConditionListItemDataObject} item - Condition item object.
	onDelete: PropTypes.func, // Arguments: GUI ID of the item.
	onInsertItem: PropTypes.func, // Arguments: Ordinal to insert the new item to; 'above'|'below'
	onInsertGroup: PropTypes.func, // Arguments: Ordinal to insert the new group to (parenthesis); 'above'|'below'
	onValidationErrorsChange: PropTypes.func, // Arguments: Item's GUIID; Updated validation errors
}

/**
 * Define component default values for own props
 */
ConditionListItem.defaultProps = {
	styleName: 'default',
	className: '',
	readOnly: false,
	showActions: true,
	enableParenthesis: false,
	clearValueType: CONDITION_LIST_VALUE_CLEAR_TYPE.ON_TYPE_CHANGE,
	emptyValues: CONDITION_LIST_FIELD_ITEM_EMPTY_VALUES,
};

export default ConditionListItem;